import { __assign, __makeTemplateObject, __read, __rest } from "tslib";
import React, { useRef, useState, useEffect, useContext } from 'react';
import { FocusOn } from 'react-focus-on';
import { Transition } from 'react-transition-group';
import { Manager, Reference, Popper } from 'react-popper';
import { css, keyframes } from 'styled-components';
import { Box } from '../Box';
import Portal from '../Portal';
import { Icon } from '../Icon';
import { useKeyPress } from '../hooks';
import { createComponent, findNextFocusableElement, findPreviousFocusableElement } from '../utils';
var DropdownContext = React.createContext({});
export var PLACEMENT_TRANSITION_ORIGINS = {
    'top-start': '0 100%',
    top: '50% 100%',
    'top-end': '100% 100%',
    'right-start': '0 0',
    right: '0 50%',
    'right-end': '0 100%',
    'bottom-start': '0 0',
    bottom: '50% 0',
    'bottom-end': '100% 0',
    'left-start': '100% 0',
    left: '100% 50%',
    'left-end': '100% 100%',
};
var ARROW_KEYS = ['ArrowUp', 'ArrowDown'];
var Trigger = createComponent({
    name: 'DropdownTrigger',
    style: css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    align-self: flex-start;\n    display: inline-block;\n    outline: none;\n  "], ["\n    align-self: flex-start;\n    display: inline-block;\n    outline: none;\n  "]))),
});
var Dropdown = function (_a) {
    var _b = _a.autoclose, autoclose = _b === void 0 ? true : _b, _c = _a.placement, placement = _c === void 0 ? 'bottom-start' : _c, _d = _a.positionFixed, positionFixed = _d === void 0 ? false : _d, _e = _a.boundariesElement, boundariesElement = _e === void 0 ? 'viewport' : _e, _f = _a.offset, offset = _f === void 0 ? '0, 10' : _f, portalNode = _a.portalNode, _g = _a.styles, styles = _g === void 0 ? {} : _g, trigger = _a.trigger, render = _a.render, children = _a.children, menuProps = __rest(_a, ["autoclose", "placement", "positionFixed", "boundariesElement", "offset", "portalNode", "styles", "trigger", "render", "children"]);
    var popperRef = useRef();
    var menuRef = useRef();
    var triggerRef = useRef();
    var _h = __read(useState(false), 2), isOpen = _h[0], setOpen = _h[1];
    var open = function () { return setOpen(true); };
    var close = function () { return setOpen(false); };
    var toggle = function () { return (isOpen ? close() : open()); };
    var handleTrigger = function (e) {
        if (e.type === 'click' || (e.type === 'keypress' && (e.which === 13 || e.which === 32))) {
            e.preventDefault();
            e.stopPropagation();
            toggle();
        }
    };
    useEffect(function () {
        if (isOpen) {
            setTimeout(function () {
                if (menuRef === null || menuRef === void 0 ? void 0 : menuRef.current) {
                    menuRef.current.focus();
                }
            });
        }
    }, [isOpen]);
    useKeyPress('Escape', function () {
        if (isOpen) {
            if (triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) {
                triggerRef.current.focus();
            }
            close();
        }
    });
    useKeyPress(ARROW_KEYS, function (event) {
        if (isOpen && menuRef.current) {
            event.preventDefault();
            var nextFocusable = event.key === 'ArrowUp'
                ? findPreviousFocusableElement(menuRef.current, document.activeElement)
                : findNextFocusableElement(menuRef.current, document.activeElement);
            if (nextFocusable) {
                nextFocusable.focus();
            }
        }
    });
    var handleMenuBlur = function (e) {
        if (autoclose && menuRef.current && !menuRef.current.contains(e.relatedTarget)) {
            close();
        }
    };
    var renderer = render || children;
    return (React.createElement(DropdownContext.Provider, { value: { close: close } },
        React.createElement(Manager, null,
            React.createElement(Reference, null, function (_a) {
                var passedTriggerRef = _a.ref;
                return (React.createElement(Trigger, { style: styles.Trigger, ref: passedTriggerRef, tabIndex: -1 }, React.cloneElement(trigger, {
                    role: trigger.role || 'button',
                    tabIndex: trigger.tabIndex || 0,
                    'aria-haspopup': true,
                    'aria-expanded': isOpen,
                    onClick: handleTrigger,
                    onKeyPress: handleTrigger,
                    ref: triggerRef,
                    style: __assign({ cursor: 'pointer' }, (trigger.style || {})),
                })));
            }),
            isOpen && (React.createElement(Portal, { node: portalNode },
                React.createElement(Popper, { innerRef: popperRef, placement: placement, positionFixed: positionFixed, modifiers: {
                        offset: {
                            offset: offset,
                        },
                        flip: {
                            enabled: false,
                        },
                        preventOverflow: {
                            boundariesElement: boundariesElement,
                            padding: 12,
                        },
                        computeStyle: { gpuAcceleration: false },
                    } }, function (_a) {
                    var ref = _a.ref, style = _a.style;
                    return (React.createElement(Transition, { in: isOpen, timeout: 0, appear: true }, function (state) { return (React.createElement(FocusOn, { enabled: isOpen, autoFocus: false },
                        React.createElement(DropdownMenu, __assign({ ref: function (menuInner) {
                                menuRef.current = menuInner;
                                ref(menuInner);
                            }, style: style, placement: placement, transitionState: state, onBlur: handleMenuBlur, "data-testid": "dropdown-menu" }, menuProps), typeof renderer === 'function'
                            ? renderer({
                                close: close,
                            })
                            : renderer))); }));
                }))))));
};
export default Dropdown;
var scaleIn = keyframes(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  from {\n    opacity: 0.75;\n    transform: scale(0.75);\n  }\n  to {\n    opacity: 1;\n    transform: scale(1);\n  }\n"], ["\n  from {\n    opacity: 0.75;\n    transform: scale(0.75);\n  }\n  to {\n    opacity: 1;\n    transform: scale(1);\n  }\n"])));
var DropdownMenu = createComponent({
    name: 'DropdownMenu',
    props: function () { return ({
        tabIndex: 0,
    }); },
    style: function (_a) {
        var width = _a.width, theme = _a.theme, placement = _a.placement, zIndex = _a.zIndex, transitionState = _a.transitionState, transitionDuration = _a.transitionDuration, transitionTimingFunction = _a.transitionTimingFunction;
        return css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    z-index: ", ";\n    background: white;\n    border-radius: ", "px;\n    border: 1px solid ", ";\n    outline: none;\n    box-shadow: ", ";\n    width: ", ";\n    opacity: 0.75;\n    transform: scale(0.75);\n    transform-origin: ", ";\n    padding: 8px;\n\n    ", "\n  "], ["\n    z-index: ", ";\n    background: white;\n    border-radius: ", "px;\n    border: 1px solid ", ";\n    outline: none;\n    box-shadow: ", ";\n    width: ", ";\n    opacity: 0.75;\n    transform: scale(0.75);\n    transform-origin: ", ";\n    padding: 8px;\n\n    ",
            "\n  "])), zIndex, theme.radius, theme.colors.greyLighter, theme.shadow.soft, typeof width === 'string' ? width : width + "px", placement ? PLACEMENT_TRANSITION_ORIGINS[placement] : 'top', (transitionState === 'entering' || transitionState === 'entered') && css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n      animation: ", " ", "ms ", " forwards;\n    "], ["\n      animation: ", " ", "ms ", " forwards;\n    "])), scaleIn, transitionDuration, transitionTimingFunction));
    },
});
Dropdown.Divider = createComponent({
    name: 'DropdownDivider',
    tag: 'hr',
    style: function (_a) {
        var theme = _a.theme;
        return css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n    background: ", ";\n    height: 1px;\n    border: 0;\n    margin-left: -8px;\n    margin-right: -8px;\n  "], ["\n    background: ", ";\n    height: 1px;\n    border: 0;\n    margin-left: -8px;\n    margin-right: -8px;\n  "])), theme.colors.greyLight);
    },
});
Dropdown.Title = createComponent({
    name: 'DropdownTitle',
    tag: 'span',
    style: function (_a) {
        var theme = _a.theme;
        return css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    display: block;\n    font-weight: 700;\n    color: ", ";\n  "], ["\n    display: block;\n    font-weight: 700;\n    color: ", ";\n  "])), theme.colors.greyDark);
    },
});
var StyledDropdownItem = createComponent({
    name: 'DropdownItem',
    props: function (_a) {
        var disabled = _a.disabled;
        return ({
            tabIndex: disabled ? -1 : 0,
            role: 'button',
        });
    },
    as: Box,
    style: function (_a) {
        var disabled = _a.disabled, theme = _a.theme, color = _a.color, icon = _a.icon, iconProps = _a.iconProps, selected = _a.selected;
        return css(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n    display: block;\n    flex: 1;\n    opacity: ", ";\n    pointer-events: ", ";\n    user-select: ", ";\n    text-decoration: none;\n    color: ", ";\n    cursor: pointer;\n    margin: 0;\n    padding: 8px;\n    transition: 125ms background;\n    outline: none;\n    appearance: none;\n    border: 0;\n    border-radius: ", "px;\n    font: inherit;\n    font-size: 14px;\n    font-weight: 500;\n    text-align: left;\n    position: relative;\n\n    &:hover,\n    &:focus {\n      color: ", ";\n      background: ", ";\n    }\n\n    ", "\n\n    ", "\n  "], ["\n    display: block;\n    flex: 1;\n    opacity: ", ";\n    pointer-events: ", ";\n    user-select: ", ";\n    text-decoration: none;\n    color: ", ";\n    cursor: pointer;\n    margin: 0;\n    padding: 8px;\n    transition: 125ms background;\n    outline: none;\n    appearance: none;\n    border: 0;\n    border-radius: ", "px;\n    font: inherit;\n    font-size: 14px;\n    font-weight: 500;\n    text-align: left;\n    position: relative;\n\n    &:hover,\n    &:focus {\n      color: ", ";\n      background: ", ";\n    }\n\n    ",
            "\n\n    ",
            "\n  "])), disabled ? 0.3 : 1, disabled ? 'none' : 'initial', disabled ? 'none' : 'initial', color || theme.colors.greyDarkest, theme.radius, color || theme.colors.greyDarkest, theme.colors.greyLightest, icon && css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n      padding-left: ", "px;\n    "], ["\n      padding-left: ", "px;\n    "])), (iconProps.size || 16) + 16), selected && css(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n      color: ", ";\n\n      &:hover,\n      &:active,\n      &:focus {\n        color: ", ";\n      }\n    "], ["\n      color: ", ";\n\n      &:hover,\n      &:active,\n      &:focus {\n        color: ", ";\n      }\n    "])), theme.colors.primary, theme.colors.primary));
    },
});
var StyledIcon = createComponent({
    name: 'DropdownIcon',
    as: Icon,
    style: css(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n    position: absolute;\n    left: 8px;\n  "], ["\n    position: absolute;\n    left: 8px;\n  "]))),
});
var DropdownItem = function (_a) {
    var _b = _a.closeOnClick, closeOnClick = _b === void 0 ? true : _b, onClick = _a.onClick, children = _a.children, icon = _a.icon, _c = _a.iconProps, iconProps = _c === void 0 ? {} : _c, props = __rest(_a, ["closeOnClick", "onClick", "children", "icon", "iconProps"]);
    var close = useContext(DropdownContext).close;
    var handleClick = function () {
        if (closeOnClick) {
            close();
        }
        if (typeof onClick === 'function') {
            onClick();
        }
    };
    return (React.createElement(StyledDropdownItem, __assign({ onClick: handleClick, icon: icon, iconProps: iconProps }, props),
        icon && React.createElement(StyledIcon, __assign({ name: icon }, iconProps)),
        children));
};
Dropdown.Item = DropdownItem;
Dropdown.defaultProps = {
    placement: 'bottom-start',
    autoclose: true,
    offset: '0, 10',
    positionFixed: false,
    boundariesElement: 'viewport',
    width: 'auto',
    zIndex: 10,
    transitionDuration: 225,
    transitionTimingFunction: 'cubic-bezier(0.25, 0.1, 0.17, 1.2)',
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10;
//# sourceMappingURL=Dropdown.js.map